import {
  HttpClient,
  HttpErrorResponse,
  HttpEventType,
  HttpHeaders,
  type HttpDownloadProgressEvent,
  type HttpHeaderResponse,
} from '@angular/common/http';
import { Subscription } from 'rxjs';
import type { RagAskStreamMode } from '../config/rag-ask.config';
import type { AskRequestBody } from '../models/ask.models';

/**
 * Keep the host's interceptor chain while adapting HttpClient to the existing
 * JSON/SSE/NDJSON reader. RxJS is confined to the HTTP boundary; state remains
 * signal-based. Unsubscribing aborts the underlying Angular request.
 */
export function requestAskWithHttpClient(
  http: HttpClient,
  url: string,
  body: AskRequestBody,
  headers: Record<string, string>,
  withCredentials: boolean,
  streamMode: RagAskStreamMode,
  signal: AbortSignal,
): Promise<Response> {
  return new Promise<Response>((resolve, reject) => {
    const subscription = new Subscription();
    const encoder = new TextEncoder();
    let controller: ReadableStreamDefaultController<Uint8Array> | undefined;
    let responseStarted = false;
    let finished = false;
    let consumed = 0;

    const cleanup = (): void => {
      signal.removeEventListener('abort', abort);
      subscription.unsubscribe();
    };
    const fail = (error: unknown): void => {
      if (finished) return;
      finished = true;
      if (controller) controller.error(error);
      else reject(error);
      cleanup();
    };
    const abort = (): void => fail(new DOMException('Request aborted', 'AbortError'));

    // Angular progress events contain the full text received so far. Forward
    // only the new suffix and keep incomplete UTF-16 pairs for the next event.
    const append = (text: string, final = false): void => {
      if (!controller) return;
      let end = text.length;
      const last = text.charCodeAt(end - 1);
      if (!final && last >= 0xd800 && last <= 0xdbff) end--;
      if (end <= consumed) return;
      controller.enqueue(encoder.encode(text.slice(consumed, end)));
      consumed = end;
    };

    const startStream = (event: HttpHeaderResponse): void => {
      const type = event.headers.get('content-type') ?? '';
      const streaming = streamMode === 'stream' ||
        (streamMode === 'auto' && /text\/event-stream|(?:x-)?ndjson/i.test(type));
      // Let interceptors handle failures (including token refresh) before
      // exposing a response. No error is retried through an anonymous fetch.
      if (responseStarted || !event.ok || event.status === 204 || event.status === 205 || !streaming) return;
      const stream = new ReadableStream<Uint8Array>({
        start: (value) => { controller = value; },
        cancel: () => { finished = true; cleanup(); },
      });
      responseStarted = true;
      resolve(new Response(stream, responseInit(event.status, event.headers)));
    };

    if (signal.aborted) { abort(); return; }
    signal.addEventListener('abort', abort, { once: true });

    subscription.add(http.request('POST', url, {
      body,
      headers,
      withCredentials,
      observe: 'events',
      responseType: 'text',
      reportProgress: streamMode !== 'json',
    }).subscribe({
      next: (event) => {
        if (finished) return;
        if (event.type === HttpEventType.ResponseHeader) {
          startStream(event);
        } else if (event.type === HttpEventType.DownloadProgress) {
          const partialText = (event as HttpDownloadProgressEvent).partialText;
          if (typeof partialText === 'string') append(partialText);
        } else if (event.type === HttpEventType.Response) {
          if (controller) {
            append(event.body ?? '', true);
            controller.close();
          } else {
            responseStarted = true;
            resolve(new Response(
              event.status === 204 || event.status === 205 ? null : event.body,
              responseInit(event.status, event.headers),
            ));
          }
          finished = true;
          cleanup();
        }
      },
      error: (error: unknown) => {
        if (finished) return;
        if (error instanceof HttpErrorResponse && error.status >= 400 &&
            error.status <= 599 && !responseStarted) {
          // Keep the DRF error body so the service can show its detail message.
          const payload = typeof error.error === 'string'
            ? error.error : JSON.stringify(error.error ?? {});
          finished = true;
          resolve(new Response(payload, responseInit(error.status, error.headers)));
          cleanup();
        } else {
          fail(error);
        }
      },
      complete: () => {
        if (!finished) fail(new TypeError('The HTTP request completed without a response.'));
      },
    }));
  });
}

function responseInit(status: number, source: HttpHeaders): ResponseInit {
  const headers: Record<string, string> = {};
  source.keys().forEach((key) => { headers[key] = source.getAll(key)!.join(', '); });
  return { status, headers };
}
